import {
  Breadcrumb,
  BreadcrumbEllipsis,
  BreadcrumbItem,
  BreadcrumbList,
  BreadcrumbSeparator
} from "@/components/ui/breadcrumb";
import { ChevronLeft } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger
} from "@/components/ui/dropdown-menu";
import useBoundStore from "@/services/store/BoundStore";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger
} from "@/components/ui/tooltip";
import Text from "@/components/ui/text";

export interface UserBreadcrumbsProps {
  title: string;
  subtitle: string;
  ids: string[];
  setter: (id: string) => void;
}

const UserBreadcrumbs = ({
  title,
  subtitle,
  ids,
  setter
}: UserBreadcrumbsProps) => {
  const getUser = useBoundStore((state) => state.users.getEntity);
  const numBreadcrumbs = ids.length;
  const navigateToBreadcrumbId = (idx: number) => {
    setter(ids[idx]);
  };
  const prevUserName = getUser(ids[numBreadcrumbs - 2])?.displayName ?? "";
  return (
    <>
      {/* If we're at the head of the breadcrumbs */}
      {numBreadcrumbs < 2 && (
        <>
          <Text as="div" variant="lgSubtitleH1" className="py-1 px-0">
            {title}
          </Text>
          <Text as="div" variant="lgBodySecondary" className="py-1 px-0">
            {subtitle}
          </Text>
        </>
      )}
      {/* If we started down the path of the breadcrumbs */}
      {numBreadcrumbs >= 2 && (
        <>
          <Text as="div" variant="bodySecondary" className="py-1 px-0">
            <Breadcrumb>
              <BreadcrumbList className="max-w-[486px] xl:max-w-full">
                <BreadcrumbSeparator
                  className="cursor-pointer"
                  onClick={() => navigateToBreadcrumbId(numBreadcrumbs - 2)}
                >
                  <ChevronLeft />
                </BreadcrumbSeparator>
                {/* Special case - display title instead of user's name for the second step */}
                {numBreadcrumbs >= 2 && numBreadcrumbs <= 3 ? (
                  <BreadcrumbItem
                    className="cursor-pointer"
                    onClick={() => navigateToBreadcrumbId(0)}
                  >
                    {`${title} - ${subtitle}`}
                  </BreadcrumbItem>
                ) : (
                  <DropdownMenu>
                    <DropdownMenuTrigger>
                      <BreadcrumbEllipsis />
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="start">
                      {ids.slice(1, -1).map((id: string, index: number) => {
                        const user = getUser(id);
                        if (user)
                          return (
                            <DropdownMenuItem
                              key={id}
                              onClick={() => navigateToBreadcrumbId(index + 1)}
                            >
                              {user.displayName}
                            </DropdownMenuItem>
                          );
                        return;
                      })}
                    </DropdownMenuContent>
                  </DropdownMenu>
                )}
                {/* If we're beyond the second step, add a menu for easy navigation */}
                {numBreadcrumbs > 2 && (
                  <>
                    <BreadcrumbSeparator>/</BreadcrumbSeparator>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <BreadcrumbItem
                          className="cursor-pointer"
                          onClick={() =>
                            navigateToBreadcrumbId(numBreadcrumbs - 2)
                          }
                        >
                          {prevUserName}
                        </BreadcrumbItem>
                      </TooltipTrigger>
                      <TooltipContent>{prevUserName}</TooltipContent>
                    </Tooltip>
                  </>
                )}
              </BreadcrumbList>
            </Breadcrumb>
          </Text>
          <Text as="div" variant="lgSubtitleH3" className="py-2 px-0">
            {getUser(ids[numBreadcrumbs - 1])?.displayName}
          </Text>
        </>
      )}
    </>
  );
};

export default UserBreadcrumbs;
